import { forwardRef, ReactNode } from 'react';

import { getDOMProps, isRenderable } from 'app/shared/utils/react';
import { useControlled } from 'app/shared/hooks/useControlled';
import styled, { css } from 'styled-components';

// Components
import { Card, CardContent, Collapse } from '@mui/material';
import {
  U21SectionHeader,
  U21SectionHeaderProps,
} from 'app/shared/u21-ui/components/layout/section/U21SectionHeader';

export interface U21SectionProps extends Omit<
  U21SectionHeaderProps,
  'children' | 'icon' | 'title'
> {
  children?: ReactNode;
  title?: U21SectionHeaderProps['children'];
  titleIcon?: U21SectionHeaderProps['icon'];
}

const U21SectionBase = forwardRef<HTMLDivElement, U21SectionProps>(
  (props, ref) => {
    const {
      action,
      children,
      collapsed: collapsedProp = false,
      collapsible = false,
      disableTitleCase = false,
      helpModalProps,
      mounted = false,
      onToggleCollapse: onToggleCollapseProp,
      subheader,
      title,
      titleIcon,
      ...rest
    } = props;

    const hasTitle = Boolean(title);

    const [collapsed, onToggleCollapse] = useControlled(
      collapsedProp,
      onToggleCollapseProp,
    );

    return (
      <StyledCard ref={ref} {...getDOMProps(rest)}>
        <Content>
          {hasTitle && (
            <StyledU21SectionHeader
              action={action}
              collapsed={collapsed}
              collapsible={collapsible}
              disableTitleCase={disableTitleCase}
              helpModalProps={helpModalProps}
              icon={titleIcon}
              onToggleCollapse={onToggleCollapse}
              subheader={subheader}
            >
              {title}
            </StyledU21SectionHeader>
          )}
          {(() => {
            if (!isRenderable(children)) {
              return null;
            }
            if (collapsible) {
              return (
                <Collapse
                  in={!collapsed}
                  mountOnEnter={!mounted}
                  unmountOnExit={!mounted}
                >
                  <StyledCardContent $hasTitle>{children}</StyledCardContent>
                </Collapse>
              );
            }
            return (
              <StyledCardContent $hasTitle={hasTitle}>
                {children}
              </StyledCardContent>
            );
          })()}
        </Content>
      </StyledCard>
    );
  },
);

const StyledCard = styled(Card)`
  display: flex;
  max-width: 100%;
  padding: 0;
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
`;

const StyledU21SectionHeader = styled(U21SectionHeader)`
  padding: 24px;
`;

const StyledCardContent = styled(CardContent)<{ $hasTitle?: boolean }>`
  flex: 1;

  ${(props) =>
    props.$hasTitle &&
    css`
      padding-top: 0;
    `}
`;

export const U21Section = Object.assign(U21SectionBase, {
  Content,
});
